// Value types shared by several collections. Source: GroundTeam/Support/Geo.swift (`struct Coordinate`).

import type { JsonObject } from '../wire';
import { DOUBLE, decodeModel, field, objectOf, type Decoded, type FieldMap, type Passthrough } from './fields';

/** `Coordinate` (records, activities, territory polygons, routes, day-log points). */
export interface CoordinatePayload extends Passthrough {
  latitude: number;
  longitude: number;
}

export const COORDINATE_KEYS = ['latitude', 'longitude'] as const satisfies readonly (keyof CoordinatePayload)[];

/**
 * An Optional coordinate (`Record.coordinate?`, `Activity.location?`, `Route.start?`): there is no template instance, so
 * a missing latitude or longitude fails the decode, as in Swift.
 */
export const COORDINATE_FIELDS: FieldMap<CoordinatePayload> = {
  latitude: field.strict(DOUBLE),
  longitude: field.strict(DOUBLE),
};
export const COORDINATE = objectOf<CoordinatePayload>('Coordinate', COORDINATE_FIELDS);

/**
 * A coordinate inside a template element (`Territory.polygon[]`, `TrackPoint.coordinate`): the decoding templates hold
 * `Coordinate(latitude: 0, longitude: 0)` there, so ResilientDecoding.merge fills a missing half with 0.
 */
export const TEMPLATED_COORDINATE = objectOf<CoordinatePayload>('Coordinate', {
  latitude: field.req(DOUBLE, () => 0),
  longitude: field.req(DOUBLE, () => 0),
});

export function decodeCoordinate(raw: JsonObject): Decoded<CoordinatePayload> {
  return decodeModel(COORDINATE_FIELDS, raw, { hasId: false });
}

export function newCoordinate(latitude: number, longitude: number): CoordinatePayload {
  return COORDINATE.write({ latitude, longitude }, 'coordinate');
}
